import React, { useMemo } from 'react';
import { Card, CardHeader, CardContent, Box } from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import { IPersonalData } from '../../../../constants/types';
import { chartColors } from '../config/analyticsCharts';


interface ChartProps {
  data: IPersonalData[];
  title?: string;
}

// AgeGroupChart Component
export const AgeGroupChart: React.FC<ChartProps> = ({ data: patients, title = 'Age Distribution' }) => {
  const ageGroupData = useMemo(() => {
    const ageGroups: Record<string, number> = {
      '0-17': 0,
      '18-30': 0,
      '31-45': 0,
      '46-60': 0,
      '61-75': 0,
      '75+': 0
    };

    patients.forEach(patient => {
      const birthDate = new Date(patient.birthdate);
      const age = new Date().getFullYear() - birthDate.getFullYear();
      
      if (age <= 17) ageGroups['0-17']++;
      else if (age <= 30) ageGroups['18-30']++;
      else if (age <= 45) ageGroups['31-45']++;
      else if (age <= 60) ageGroups['46-60']++;
      else if (age <= 75) ageGroups['61-75']++;
      else ageGroups['75+']++;
    });

    // Get the total count for percentage calculation
    const total = Object.values(ageGroups).reduce((sum, count) => sum + count, 0);
    
    // Store original counts for tooltip reference
    const originalCounts = {...ageGroups};
    
    // Convert counts to percentages
    const percentages = Object.entries(ageGroups).reduce((acc, [key, value]) => {
      acc[key] = total > 0 ? (value / total) * 100 : 0;
      return acc;
    }, {} as Record<string, number>);

    return {
      labels: Object.keys(ageGroups),
      datasets: [{
        label: 'Percentage of Patients',
        data: Object.values(percentages).map(value => Math.round(value)),
        backgroundColor: `${chartColors.seriesColors[0]}80`, // Single color with transparency
        borderColor: chartColors.seriesColors[0], // Single color for border
        borderWidth: 1,
        // Store original counts as metadata for tooltip
        originalData: Object.values(originalCounts)
      }]
    };
  }, [patients]);

  const options: {
    responsive: boolean;
    maintainAspectRatio: boolean;
    scales: {
      y: {
        beginAtZero: boolean;
        max?: number;
        ticks: {
          stepSize?: number;
          callback?: (value: number) => string;
        };
      };
    };
    plugins: {
      legend: {
        position: 'top';
      };
      title: {
        display: boolean;
      };
      tooltip: {
        callbacks: {
          label: (context: any) => string;
        };
      };
    };
  } = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            // Display the percentage with 1 decimal place
            const percentage = parseFloat(context.raw as string).toFixed(1);
            // Get the original count from the metadata
            const index = context.dataIndex;
            const count = (context.dataset as any).originalData[index];
            
            return `${context.dataset.label}: ${percentage}% (${count} patients)`;
          }
        }
      }
    }
  };

  return (
    <Card>
      <CardHeader title={title} />
      <CardContent>
        <Box height={300}>
          <Bar data={ageGroupData} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};

// SexDistributionChart Component
export const SexDistributionChart: React.FC<ChartProps> = ({ data: patients, title = 'Sex Distribution' }) => {
  const genderData = useMemo(() => {
    const distribution: Record<string, number> = patients.reduce((acc, patient) => {
      const gender = patient.sex;
      acc[gender] = (acc[gender] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      labels: ['Male', 'Female'],
      datasets: [{
        data: [distribution['Male'] || 0, distribution['Female'] || 0],
        backgroundColor: [
          `${chartColors.seriesColors[0]}80`, // Blue with transparency
          `${chartColors.seriesColors[3]}80`, // Amber with transparency
        ],
        borderColor: [
          chartColors.seriesColors[0], // Blue
          chartColors.seriesColors[3], // Amber
        ],
        borderWidth: 1,
      }]
    };
  }, [patients]);

  const options: {
    responsive: boolean;
    maintainAspectRatio: boolean;
    plugins: {
      legend: {
        position: 'top';
      };
      tooltip: {
        callbacks: {
          label: (context: any) => string;
        };
      };
    };
  } = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = genderData.datasets[0].data.reduce((a, b) => a + b, 0);
            const percentage = ((context.raw / total) * 100).toFixed(1);
            return `${context.label}: ${context.raw} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <Card>
      <CardHeader title={title} />
      <CardContent>
        <Box height={300}>
          <Pie data={genderData} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};